import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Save, Upload, X } from 'lucide-react';
import { aboutAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const AdminAbout = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadedImage, setUploadedImage] = useState('');
  const [formData, setFormData] = useState({
    bio: '',
    skills: '',
    profile_image: ''
  });

  useEffect(() => {
    loadAbout();
  }, []);

  const loadAbout = async () => {
    try {
      const response = await aboutAPI.get();
      setFormData({
        bio: response.data.bio || '',
        skills: response.data.skills?.join(', ') || '',
        profile_image: response.data.profile_image || ''
      });
    } catch (error) {
      console.error('Error loading about:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const updateData = {
      bio: formData.bio,
      skills: formData.skills.split(',').map(skill => skill.trim()).filter(skill => skill),
      profile_image: formData.profile_image
    };

    try {
      await aboutAPI.update(updateData);
      toast.success('About section updated successfully!');
    } catch (error) {
      toast.error('Failed to update about section');
      console.error('Error updating about:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setUploadedImage(base64String);
        setFormData({ ...formData, profile_image: base64String });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeUploadedImage = () => {
    setUploadedImage('');
    setFormData({ ...formData, profile_image: '' });
  };

  if (loading) {
    return (
      <div className="text-center py-20" data-testid="about-loading">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-about-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className="text-4xl font-bold font-['Syne'] mb-8" data-testid="about-title">Edit About Section</h1>

        <form onSubmit={handleSubmit} className="max-w-2xl space-y-6" data-testid="about-form">
          <div>
            <label className="text-sm font-medium mb-2 block">Bio</label>
            <Textarea
              value={formData.bio}
              onChange={(e) => handleChange('bio', e.target.value)}
              rows={6}
              placeholder="Tell your story..."
              data-testid="bio-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Skills (comma-separated)</label>
            <Input
              value={formData.skills}
              onChange={(e) => handleChange('skills', e.target.value)}
              placeholder="Branding, Web Design, UI/UX, Illustration"
              data-testid="skills-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Profile Image</label>
            
            {/* Image Upload Section */}
            <div className="space-y-4">
              <div className="border-2 border-dashed border-white/10 rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="profile-image-upload"
                />
                <label htmlFor="profile-image-upload" className="cursor-pointer">
                  <Upload className="mx-auto mb-2 text-muted-foreground" size={32} />
                  <p className="text-sm text-muted-foreground">Click to upload profile image</p>
                  <p className="text-xs text-muted-foreground mt-1">PNG, JPG up to 10MB</p>
                </label>
              </div>

              {/* Preview */}
              {(uploadedImage || formData.profile_image) && (
                <div className="relative inline-block">
                  <img
                    src={uploadedImage || formData.profile_image}
                    alt="Profile preview"
                    className="w-32 h-32 object-cover rounded-lg border border-white/10"
                  />
                  <button
                    type="button"
                    onClick={removeUploadedImage}
                    className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1 hover:bg-destructive/90"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-white/10" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or paste URL</span>
                </div>
              </div>

              <Input
                value={formData.profile_image.startsWith('data:') ? '' : formData.profile_image}
                onChange={(e) => {
                  setUploadedImage('');
                  handleChange('profile_image', e.target.value);
                }}
                placeholder="https://..."
                data-testid="profile-image-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={saving}
            className="bg-primary text-black hover:bg-primary/90"
            data-testid="save-about-button"
          >
            {saving ? 'Saving...' : (
              <>
                <Save size={18} className="mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

export default AdminAbout;
